import React, { useState } from 'react';
import { Row, Col, Card, Modal, Form, Button, Spinner, InputGroup } from 'react-bootstrap';
import { FaUser, FaEnvelope, FaPhone, FaUserTag, FaCalendarAlt, FaEdit, FaLock, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useAuth } from '../hooks/useAuth';
import { useApi } from '../context/ApiContext';
import { format } from 'date-fns';
import { toast } from 'react-toastify';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const { apiCall } = useApi();
  
  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Form states
  const [editForm, setEditForm] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    phone: user?.phone || ''
  });
  
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const getRoleText = (role) => {
    const roleMap = {
      'admin': 'Yönetici',
      'visa_office': 'Danışmanlık Firması',
      'user': 'Kullanıcı'
    };
    return roleMap[role] || role;
  };

  const getRoleBadgeColor = (role) => {
    const colorMap = {
      'admin': 'danger',
      'visa_office': 'warning',
      'user': 'primary'
    };
    return colorMap[role] || 'secondary';
  };
  
  const handleEditModalOpen = () => {
    setEditForm({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      phone: user?.phone || ''
    });
    setShowEditModal(true);
  };
  
  const handleEditModalClose = () => {
    setShowEditModal(false);
    setEditForm({
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      phone: user?.phone || ''
    });
  };
  
  const handlePasswordModalClose = () => {
    setShowPasswordModal(false);
    setPasswordForm({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
    setShowPasswords({
      current: false,
      new: false,
      confirm: false
    });
  };
  
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!editForm.first_name.trim() || !editForm.last_name.trim()) {
      toast.error('Ad ve soyad alanları zorunludur');
      return;
    }
    
    setLoading(true);
    try {
      // Update user profile
      const response = await apiCall(`/api/panel-users/${user.id}`, {
        method: 'PUT',
        data: {
          first_name: editForm.first_name.trim(),
          last_name: editForm.last_name.trim(),
          phone: editForm.phone.trim()
        }
      });
      
      if (response.success) {
        updateUser({
          first_name: editForm.first_name.trim(),
          last_name: editForm.last_name.trim(),
          phone: editForm.phone.trim()
        });
        toast.success('Profil bilgileri güncellendi');
        handleEditModalClose();
      }
    } catch (error) {
      console.error('Profil güncelleme hatası:', error);
      toast.error(error.message || 'Profil güncellenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password) {
      toast.error('Tüm alanları doldurun');
      return;
    }
    
    if (passwordForm.new_password.length < 6) {
      toast.error('Yeni şifre en az 6 karakter olmalıdır');
      return;
    }
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }
    
    setLoading(true);
    try {
      const response = await apiCall(`/api/panel-users/${user.id}/change-password`, {
        method: 'PUT',
        data: {
          current_password: passwordForm.current_password,
          new_password: passwordForm.new_password
        }
      });
      
      if (response.success) {
        toast.success('Şifre başarıyla değiştirildi');
        handlePasswordModalClose();
      }
    } catch (error) {
      console.error('Şifre değiştirme hatası:', error);
      toast.error(error.message || 'Şifre değiştirilirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      {/* Page Header with Actions */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h1 className="h3 mb-1">Profil</h1>
          <p className="text-muted mb-0">
            Profil bilgilerinizi görüntüleyin ve düzenleyin
          </p>
        </div>
        <div className="d-flex gap-2 profile-header-actions">
          <Button variant="primary" onClick={handleEditModalOpen} className="d-flex align-items-center justify-content-center">
            <FaEdit className="me-2" />
            <span className="d-none d-sm-inline">Profili </span>Düzenle
          </Button>
          <Button variant="outline-secondary" onClick={() => setShowPasswordModal(true)} className="d-flex align-items-center justify-content-center">
            <FaLock className="me-2" />
            Şifre Değiştir
          </Button>
        </div>
      </div>

      <Row>
        <Col xl={4} lg={5} className="mb-4">
          {/* Profile Card */}
          <Card className="text-center">
            <Card.Body className="py-5">
              <div className="mb-4">
                <div 
                  className="rounded-circle bg-primary d-inline-flex align-items-center justify-content-center" 
                  style={{ width: '100px', height: '100px' }}
                >
                  <FaUser className="text-white" size={40} />
                </div>
              </div>
              
              <h4 className="mb-1">{user?.first_name} {user?.last_name}</h4>
              <p className="text-muted mb-3">{user?.email}</p>
              
              <div className="mb-3">
                <span className={`badge bg-${getRoleBadgeColor(user?.role)} px-3 py-2`}>
                  <FaUserTag className="me-1" />
                  {getRoleText(user?.role)}
                </span>
              </div>
              
              <hr className="my-4" />
              
              <div className="d-flex justify-content-around text-center">
                <div>
                  <div className={`badge bg-${user?.email_verified ? 'success' : 'warning'} mb-2`}>
                    {user?.email_verified ? '✓' : '✗'}
                  </div>
                  <div className="small text-muted">E-posta</div>
                </div>
                <div>
                  <div className={`badge bg-${user?.is_active ? 'success' : 'danger'} mb-2`}>
                    {user?.is_active ? '✓' : '✗'}
                  </div>
                  <div className="small text-muted">Hesap</div>
                </div>
              </div>
              
              {user?.last_login && (
                <>
                  <hr className="my-4" />
                  <div className="text-center">
                    <small className="text-muted">
                      <FaCalendarAlt className="me-1" />
                      Son Giriş: {format(new Date(user.last_login), 'dd/MM/yyyy HH:mm')}
                    </small>
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col xl={8} lg={7}>
          {/* Personal Information */}
          <Card className="mb-4">
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <FaUser className="me-2 text-primary" />
                Kişisel Bilgiler
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6} className="mb-3">
                  <div className="border-bottom pb-3">
                    <small className="text-muted d-block mb-1">Ad</small>
                    <div className="fw-medium">{user?.first_name || '-'}</div>
                  </div>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="border-bottom pb-3">
                    <small className="text-muted d-block mb-1">Soyad</small>
                    <div className="fw-medium">{user?.last_name || '-'}</div>
                  </div>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="border-bottom pb-3">
                    <small className="text-muted d-block mb-1">
                      <FaEnvelope className="me-1" />
                      E-posta
                    </small>
                    <div className="fw-medium">{user?.email || '-'}</div>
                  </div>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="border-bottom pb-3">
                    <small className="text-muted d-block mb-1">
                      <FaPhone className="me-1" />
                      Telefon
                    </small>
                    <div className="fw-medium">{user?.phone || 'Belirtilmemiş'}</div>
                  </div>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="border-bottom pb-3">
                    <small className="text-muted d-block mb-1">
                      <FaUserTag className="me-1" />
                      Rol
                    </small>
                    <div>
                      <span className={`badge bg-${getRoleBadgeColor(user?.role)}`}>
                        {getRoleText(user?.role)}
                      </span>
                    </div>
                  </div>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="border-bottom pb-3">
                    <small className="text-muted d-block mb-1">
                      <FaCalendarAlt className="me-1" />
                      Kayıt Tarihi
                    </small>
                    <div className="fw-medium">
                      {user?.created_at ? format(new Date(user.created_at), 'dd/MM/yyyy') : '-'}
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* Account Status */}
          <Card>
            <Card.Header className="bg-white">
              <h5 className="mb-0">
                <FaUserTag className="me-2 text-primary" />
                Hesap Durumu
              </h5>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md={6} className="mb-3">
                  <div className="d-flex align-items-center">
                    <div 
                      className={`rounded-circle bg-${user?.email_verified ? 'success' : 'warning'} bg-opacity-10 d-flex align-items-center justify-content-center me-3`}
                      style={{ width: '48px', height: '48px' }}
                    >
                      <FaEnvelope className={`text-${user?.email_verified ? 'success' : 'warning'}`} size={20} />
                    </div>
                    <div>
                      <div className="fw-medium">E-posta Doğrulama</div>
                      <small className="text-muted">
                        {user?.email_verified ? 'Doğrulanmış' : 'Doğrulanmamış'}
                      </small>
                    </div>
                  </div>
                </Col>
                <Col md={6} className="mb-3">
                  <div className="d-flex align-items-center">
                    <div 
                      className={`rounded-circle bg-${user?.is_active ? 'success' : 'danger'} bg-opacity-10 d-flex align-items-center justify-content-center me-3`}
                      style={{ width: '48px', height: '48px' }}
                    >
                      <FaUser className={`text-${user?.is_active ? 'success' : 'danger'}`} size={20} />
                    </div>
                    <div>
                      <div className="fw-medium">Hesap Durumu</div>
                      <small className="text-muted">
                        {user?.is_active ? 'Aktif' : 'Pasif'}
                      </small>
                    </div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      
      {/* Edit Profile Modal */}
      <Modal show={showEditModal} onHide={handleEditModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaEdit className="me-2" />
            Profili Düzenle
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleEditSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Ad *</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <FaUser />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  value={editForm.first_name}
                  onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                  required
                  placeholder="Adınızı girin"
                />
              </InputGroup>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Soyad *</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <FaUser />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  value={editForm.last_name}
                  onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                  required
                  placeholder="Soyadınızı girin"
                />
              </InputGroup>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Telefon</Form.Label>
              <InputGroup>
                <InputGroup.Text>
                  <FaPhone />
                </InputGroup.Text>
                <Form.Control
                  type="tel"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  placeholder="Telefon numaranızı girin"
                />
              </InputGroup>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleEditModalClose} disabled={loading}>
              İptal
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Güncelleniyor...
                </>
              ) : (
                'Kaydet'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
      
      {/* Change Password Modal */}
      <Modal show={showPasswordModal} onHide={handlePasswordModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaLock className="me-2" />
            Şifre Değiştir
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handlePasswordSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Mevcut Şifre *</Form.Label>
              <div style={{ position: 'relative' }}>
                <Form.Control
                  type={showPasswords.current ? 'text' : 'password'}
                  value={passwordForm.current_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                  required
                  placeholder="Mevcut şifrenizi girin"
                  style={{ paddingRight: '40px' }}
                />
                <Button
                  variant="link"
                  onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                  style={{
                    position: 'absolute',
                    right: '5px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: 'none',
                    background: 'transparent',
                    padding: '4px 8px',
                    color: '#6c757d'
                  }}
                >
                  {showPasswords.current ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </div>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Yeni Şifre *</Form.Label>
              <div style={{ position: 'relative' }}>
                <Form.Control
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwordForm.new_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                  required
                  placeholder="Yeni şifrenizi girin"
                  minLength={6}
                  style={{ paddingRight: '40px' }}
                />
                <Button
                  variant="link"
                  onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                  style={{
                    position: 'absolute',
                    right: '5px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: 'none',
                    background: 'transparent',
                    padding: '4px 8px',
                    color: '#6c757d'
                  }}
                >
                  {showPasswords.new ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </div>
              <Form.Text className="text-muted">
                Şifre en az 6 karakter olmalıdır
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Yeni Şifre (Tekrar) *</Form.Label>
              <div style={{ position: 'relative' }}>
                <Form.Control
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordForm.confirm_password}
                  onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                  required
                  placeholder="Yeni şifrenizi tekrar girin"
                  minLength={6}
                  style={{ paddingRight: '40px' }}
                />
                <Button
                  variant="link"
                  onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                  style={{
                    position: 'absolute',
                    right: '5px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: 'none',
                    background: 'transparent',
                    padding: '4px 8px',
                    color: '#6c757d'
                  }}
                >
                  {showPasswords.confirm ? <FaEyeSlash /> : <FaEye />}
                </Button>
              </div>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handlePasswordModalClose} disabled={loading}>
              İptal
            </Button>
            <Button variant="warning" type="submit" disabled={loading}>
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Değiştiriliyor...
                </>
              ) : (
                'Şifreyi Değiştir'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default Profile; 